/**
 * use-inreview — Truck Pusher InReview queue + settings data hooks
 * (Phase 11 / Task 11.1, DEC-120).
 *
 * GET /v1/inreview            (REVIEW_TICKET) — queue list w/ filters + sort.
 * GET /v1/inreview/count      (REVIEW_TICKET) — nav/tab badge count.
 * GET /v1/tenants/inreview-settings   (MANAGE_TENANT) — settings (lazy-seeded).
 * PATCH /v1/tenants/inreview-settings (MANAGE_TENANT) — partial settings update.
 */

import { useApiOpts, useApiQuery, useApiMutation } from '@lodex-pro/web-shared';
import { useQueryClient } from '@tanstack/react-query';
import type { InReviewQueueRow, InReviewSettings, InReviewFlagCode } from '@lodex-pro/shared';
import type { InAppNotification } from '@lodex-pro/web-shared/notifications';
import type { RateSource } from '@lodex-pro/shared/rate-source';

export type { InReviewQueueRow, InReviewSettings, InReviewFlagCode };

export type InReviewSortKey = 'date' | 'flags' | 'volume' | 'driver';

// ── Ticket review detail (Task 11.2) ─────────────────────────────────────────

export interface InReviewTank {
  // #60 (DEC-365) — the run-ticket row id and its tank link (null = not linked).
  readonly id?: string;
  readonly haulerTankId?: string | null;
  readonly tankName: string;
  readonly beforeBbl: number | null;
  readonly afterBbl: number | null;
  readonly topGaugeFeet: number | null;
  readonly topGaugeInches: number | null;
  readonly bottomGaugeFeet: number | null;
  readonly bottomGaugeInches: number | null;
  readonly waterVolumeBbl: number | null;
  readonly oilVolumeBbl: number | null;
  readonly colorCut: string | null;
  readonly position: number;
}

export interface InReviewAuditEvent {
  readonly source: 'job_event' | 'timeclock' | 'audit_event';
  readonly eventType: string;
  readonly occurredAt: string | null;
  readonly actorName: string | null;
  readonly actorRole: string | null;
  readonly triggeredBy: string | null;
  readonly online: boolean | null;
  readonly gpsLat: number | null;
  readonly gpsLng: number | null;
  readonly gpsAccuracyFt: number | null;
  readonly verificationResult: string | null;
  readonly distanceFromCenter: number | null;
  readonly verificationThresholdFt: number | null;
  /** Task 11.5 — human-facing detail (e.g. the send-back / escalate reason). */
  readonly detail: string | null;
}

export interface InReviewFlagDetail {
  readonly code: InReviewFlagCode;
  readonly label: string;
  readonly description: string;
  /** True when this flag must be acknowledged before the ticket can be approved. */
  readonly ackRequired: boolean;
  readonly acknowledged: boolean;
  readonly acknowledgedBy: string | null;
  readonly acknowledgedAt: string | null;
  readonly acknowledgmentNotes: string | null;
  readonly data: Record<string, unknown>;
}

/**
 * OPEN-217 (DEC-382) — a Warning the driver saw and confirmed on the phone. `message` is what
 * the driver read, with the value filled in; `value` is in the field's canonical unit.
 */
export interface DriverConfirmedWarning {
  readonly ruleId: string;
  readonly field: string;
  readonly stage: string;
  readonly position: number | null;
  readonly serviceChargeId: string | null;
  readonly value: number | null;
  readonly message: string;
  readonly confirmedAt: string;
}

/** One VALIDATION_RULE finding: a rule the server's re-check says the phone didn't settle. */
export interface ValidationRuleFinding {
  readonly ruleId: string;
  readonly field: string;
  readonly severity: 'WARNING' | 'BLOCK';
  readonly stage: string;
  readonly position: number | null;
  readonly serviceChargeId: string | null;
  readonly value: number | null;
  readonly message: string;
  readonly reason: 'BLOCK_NOT_FIXED' | 'WARNING_NOT_CONFIRMED' | 'RULE_NOT_ON_PHONE';
}

export interface InReviewDetail {
  readonly id: string;
  readonly haulId: string | null;
  readonly status: string;
  readonly jobType: string | null;
  readonly billingMethod: string | null;
  readonly uom: string | null;
  readonly inreviewAt: string | null;
  readonly inreviewFlags: InReviewFlagCode[];
  readonly truckPusherNotes: string | null;
  readonly reviewedByName: string | null;
  readonly reviewedAt: string | null;
  /**
   * DEC-321 / OPEN-082 — set when dispatch forced this stuck job into review ("Send to
   * review" on the stuck list); null otherwise. The panel shows it as a banner and offers
   * "Return to stuck list" in place of Send Back. ISO instants; render local (DEC-004).
   */
  readonly forcedReview: {
    readonly at: string;
    readonly byName: string | null;
    readonly reason: string | null;
    /** The in-progress status the job was stuck at when it was forced. */
    readonly priorStatus: string | null;
    /** Where the driver's clock was closed: their last real activity. */
    readonly clockEndAt: string | null;
    /** DEC-329 — the manager who approved the forcing; null when no approval was required. */
    readonly approvedByName?: string | null;
  } | null;
  /**
   * OPEN-169 addendum — the company allows Send Back to the driver (default OFF; locked off
   * until the phone has a correction flow). Optional: an older API build did not send it.
   */
  readonly allowSendBack?: boolean;
  readonly site: {
    readonly siteName: string | null;
    // #60 (DEC-365) — the pickup ids and the well the load came from.
    readonly tankBatteryId?: string | null;
    readonly pickupWellId?: string | null;
    readonly sourceWellId?: string | null;
    readonly sourceWellName?: string | null;
    readonly operatorName: string | null;
    readonly apiNumber: string | null;
    readonly state: string | null;
  };
  readonly driver: { readonly name: string | null; readonly phone: string | null };
  readonly dispatcher: { readonly name: string | null };
  readonly destination: {
    readonly name: string | null;
    /** Bug 2 (Corbin) — the intended (planned) and the actual disposal, side by side. */
    readonly intended?: string | null;
    readonly actual?: string | null;
    /** 0216 — the office recorded "no disposal happened"; null when it did not. */
    readonly noDisposal?: {
      readonly at: string | null;
      readonly reason: string | null;
      readonly byName: string | null;
    } | null;
  };
  readonly timing: {
    readonly createdAt: string | null;
    readonly completedAt: string | null;
    readonly inreviewAt: string | null;
  };
  readonly capture: {
    readonly netBbl: number;
    /** Task 11.4 — truck-pusher reviewed override (null = no edit; use netBbl). */
    readonly reviewedNetBbl: number | null;
    readonly reviewedNetBblReason: string | null;
    readonly reviewedBy: string | null;
    readonly reviewedAt: string | null;
    readonly capturedAt: string | null;
    readonly tankName: string | null;
    readonly producerIdentifier: string | null;
    readonly driverNotes: string | null;
    readonly tanks: readonly InReviewTank[];
    readonly signature: {
      readonly present: boolean;
      readonly key: string | null;
      readonly url: string | null;
    };
    readonly photos: readonly { readonly key: string; readonly url: string | null }[];
  };
  readonly dispatcherNotes: string | null;
  /**
   * DEC-299 / Task 18.6 — the OPERATOR's note on a haul request made through the public
   * API (jobs.operator_request_notes), verbatim. Never merged into dispatcherNotes: it is
   * rendered by OperatorRequestNote, labelled as the operator's words. Null for every job a
   * dispatcher, driver or foreman created. Optional so existing literals compile.
   */
  readonly operatorRequestNotes?: string | null;
  readonly gpsEvents: readonly {
    readonly eventType: string;
    readonly occurredAt: string | null;
    readonly lat: number | null;
    readonly lng: number | null;
    readonly accuracyFt: number | null;
    readonly verificationResult: string | null;
    readonly capturedOffline: boolean;
  }[];
  readonly auditTimeline: readonly InReviewAuditEvent[];
  readonly flagDetails: readonly InReviewFlagDetail[];
  /** OPEN-217 — the Warnings the driver confirmed. Optional so an older API reads as none. */
  readonly driverConfirmedWarnings?: readonly DriverConfirmedWarning[];
  /** False while any ack-required flag is still unacknowledged (Task 11.3). */
  readonly canApprove: boolean;
  /**
   * DEC-190/198 — what approving this ticket WOULD bill, assessed by the same
   * code that does the emitting (`assessBillability`), never re-derived here.
   * `unbillable` is null when approval will produce billable events.
   */
  readonly billing: {
    readonly billingMethod: string | null;
    readonly netBbl: number;
    readonly hours: number;
    /** The rate that actually resolved: customer → operator → job-type default. */
    readonly resolvedRateCents: number | null;
    /**
     * What Approve will bill, priced server-side from the exact events it would emit —
     * the primary row plus every service charge and line item, each rounded like the
     * invoice. Integer cents. Show this, never rate × quantity (which left the charges
     * out: $120 shown, $790 billed).
     */
    readonly estimate: {
      readonly totalCents: number;
      readonly lineCount: number;
      /** Lines with no price yet — included at $0. */
      readonly unpricedLineCount: number;
    };
    /**
     * Deep-link target for the no-rate banner — the customer whose pricing needs
     * setting. NULL on legacy-model jobs, which have no editable customer row.
     */
    readonly haulerOperatorId: string | null;
    readonly unbillable: {
      readonly reason: 'MISSING_RATE' | 'NO_SUBSTANCE' | 'JOB_NOT_FOUND';
      readonly billingMethod: string | null;
      readonly detail: string;
    } | null;
    /**
     * #52 (DEC-358) — the customer disposal fee this ticket resolves to; null when no fee
     * applies (job type's switch off, no disposal site, no volume). feeCentsPerBbl null =
     * missing (see billingWarnings); 0 = a deliberate $0.00 fee, no line. Both fields are
     * optional so this web build reads an older API during a rollout as "no fee".
     */
    readonly disposalFee?: {
      readonly site: DisposalFeeSite;
      readonly day: string;
      readonly feeCentsPerBbl: number | null;
      readonly source: 'CUSTOMER' | 'DEFAULT' | 'NONE';
      readonly effectiveFrom: string | null;
    } | null;
    /**
     * #52 — MISSING_DISPOSAL_FEE: approve refuses until the fee is set or skipped. Bug 2 —
     * DISPOSAL_NOT_RECORDED: approve refuses until the actual disposal is recorded.
     */
    readonly billingWarnings?: readonly (MissingDisposalFeeWarning | DisposalNotRecordedWarning)[];
    /**
     * gap 1 (DEC-383, D-15) — the hauling rate and where it came from (customer x job type,
     * customer rate, or the job type's standard rate); source null = no rate. Shown as the tag
     * under Effective Rate, never on the invoice.
     */
    readonly haulPrice: { readonly rateCents: number | null; readonly source: RateSource | null };
    /** The ticket's billing day ('YYYY-MM-DD'), the day the D-14 job-type fix starts its price on. */
    readonly priceDay: string | null;
    /** The company's local today ('YYYY-MM-DD'), so the tag drops this year's year. */
    readonly companyToday: string;
    /** The ticket's pinned job type: its family (the price key), name and unit. */
    readonly jobType: {
      readonly familyId: string;
      readonly label: string;
      readonly billingMethod: string;
    };
  };
}

/** #52 (DEC-358 #7) — where the water went: catalog facility, or the hauler's own location. */
export interface DisposalFeeSite {
  readonly kind: 'FACILITY' | 'LOCATION' | 'WELL';
  readonly id: string;
  readonly name: string;
}

export interface MissingDisposalFeeWarning {
  readonly code: 'MISSING_DISPOSAL_FEE';
  /** NULL on a legacy-model job: only a site default can apply. */
  readonly haulerOperatorId: string | null;
  readonly customer: string | null;
  readonly site: DisposalFeeSite;
  /** The company-local day whose fee applies, 'YYYY-MM-DD'. */
  readonly day: string;
  readonly detail: string;
}

/** Bug 2 (Corbin) — a disposal-ending load with no actual disposal recorded. */
export interface DisposalNotRecordedWarning {
  readonly code: 'DISPOSAL_NOT_RECORDED';
  /** The intended (planned) place's name, a hint only; null when none was planned. */
  readonly intended: string | null;
  readonly detail: string;
}

/** Bug 2 — record where the water actually went; the review refetches. */
export function useRecordActualDisposal(
  jobId: string,
): ReturnType<
  typeof useApiMutation<
    { target: { kind: 'FACILITY' | 'LOCATION' | 'WELL'; id: string } },
    { jobId: string; flags: readonly string[] }
  >
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<
    { target: { kind: 'FACILITY' | 'LOCATION' | 'WELL'; id: string } },
    { jobId: string; flags: readonly string[] }
  >(apiOpts, `/v1/inreview/${jobId}/actual-disposal`, 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }),
  });
}

/** 0216 — record that no disposal happened (a reason of 10+ characters); the review refetches. */
export function useRecordNoDisposal(
  jobId: string,
): ReturnType<
  typeof useApiMutation<{ reason: string }, { jobId: string; flags: readonly string[] }>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, { jobId: string; flags: readonly string[] }>(
    apiOpts,
    `/v1/inreview/${jobId}/no-disposal`,
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }) },
  );
}

/** #920 review (LOW a) — take a mistaken "no disposal happened" back (reason 10+, audited). */
export function useUndoNoDisposal(
  jobId: string,
): ReturnType<
  typeof useApiMutation<{ reason: string }, { jobId: string; flags: readonly string[] }>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, { jobId: string; flags: readonly string[] }>(
    apiOpts,
    `/v1/inreview/${jobId}/no-disposal/undo`,
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }) },
  );
}

/**
 * gap 1 D-14 / D-20 — the MISSING_RATE fix "price for <job type> for this customer". The server
 * starts it on the ticket's billing day (the body has no date). On success the review refetches,
 * so MISSING_RATE clears without a reload.
 */
export interface SetJobTypePriceBody {
  readonly rateCents: number;
  readonly notes: string | null;
}

export function useSetJobTypePriceFromReview(
  jobId: string,
): ReturnType<
  typeof useApiMutation<SetJobTypePriceBody, { id: string; effectiveFrom: string | null }>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<SetJobTypePriceBody, { id: string; effectiveFrom: string | null }>(
    apiOpts,
    `/v1/inreview/${jobId}/job-type-price`,
    'POST',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }) },
  );
}

/** Fetch a ticket's full review detail. Pass `null` to disable (panel closed). */
export function useInReviewDetail(
  jobId: string | null,
): ReturnType<typeof useApiQuery<InReviewDetail>> {
  const apiOpts = useApiOpts();
  return useApiQuery<InReviewDetail>(apiOpts, `/v1/inreview/${jobId ?? ''}`, {
    queryKey: ['inreview', 'detail', jobId ?? '__none__'],
    enabled: jobId !== null,
    staleTime: 10_000,
  });
}

/** PATCH truck-pusher notes on a ticket; refreshes the detail on success. */
export function useUpdateInReviewNotes(
  jobId: string,
): ReturnType<typeof useApiMutation<{ notes: string }, { truckPusherNotes: string | null }>> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ notes: string }, { truckPusherNotes: string | null }>(
    apiOpts,
    `/v1/inreview/${jobId}/notes`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['inreview', 'detail', jobId] });
      },
    },
  );
}

/** Edit the reviewed net BBL (Task 11.4). Refreshes the ticket detail on success. */
export function useEditVolume(
  jobId: string,
): ReturnType<
  typeof useApiMutation<
    { reviewedNetBbl: number; reason: string },
    { ok: boolean; reviewedNetBbl: number; originalNetBbl: number }
  >
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<
    { reviewedNetBbl: number; reason: string },
    { ok: boolean; reviewedNetBbl: number; originalNetBbl: number }
  >(apiOpts, `/v1/inreview/${jobId}/volume`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inreview', 'detail', jobId] });
    },
  });
}

/** Revert a reviewed-volume edit (Task 11.4). Refreshes the ticket detail on success. */
export function useRevertVolume(
  jobId: string,
): ReturnType<typeof useApiMutation<void, { ok: boolean }>> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<void, { ok: boolean }>(apiOpts, `/v1/inreview/${jobId}/volume`, 'DELETE', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inreview', 'detail', jobId] });
    },
  });
}

/**
 * Approve a ticket → TO_BE_INVOICED (Task 11.5). Invalidates the queue + count.
 *
 * The body is REQUIRED, even when empty. It used to be `void`, which made
 * `useApiMutation` send `JSON.stringify(undefined)` — i.e. no body at all — while
 * `authedFetch` still set `Content-Type: application/json`. Fastify runs the JSON
 * parser whenever a content-type is present on a PATCH (handleRequest.js), so an
 * absent body raised FST_ERR_CTP_EMPTY_JSON_BODY (400), which the error handler
 * then reported as a 500 (OPEN-040). Net effect: the Approve button returned
 * "Something went wrong" 100% of the time and no ticket was ever approvable from
 * the browser. Sending `{}` (or `{ approveNonBillable: true }`) is what makes the
 * button work at all — verified against dev, see the PR body.
 */
/** #52 — `skipDisposalFee` approves without a missing disposal fee; the reason is audited. */
export interface ApproveBody {
  readonly approveNonBillable?: boolean;
  readonly skipDisposalFee?: { readonly reason: string };
}

export function useApproveTicket(
  jobId: string,
): ReturnType<
  typeof useApiMutation<
    ApproveBody,
    { ok: boolean; status: string; unbillable: { reason: string } | null }
  >
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<
    ApproveBody,
    { ok: boolean; status: string; unbillable: { reason: string } | null }
  >(apiOpts, `/v1/inreview/${jobId}/approve`, 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }),
  });
}

/** Send a ticket back to the driver → COMPLETED (Task 11.5). Invalidates the queue. */
export function useSendBackTicket(
  jobId: string,
): ReturnType<typeof useApiMutation<{ reason: string }, { ok: boolean; driverNotified: boolean }>> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, { ok: boolean; driverNotified: boolean }>(
    apiOpts,
    `/v1/inreview/${jobId}/send-back`,
    'PATCH',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }) },
  );
}

/**
 * Return a dispatcher-forced ticket to the stuck list (DEC-321 / OPEN-082). The forced
 * ticket's counterpart to Send Back, which the API refuses on it: the job goes back to the
 * in-progress status it was forced from. Invalidates the queue and ['jobs'] (the stuck list
 * and job board, where it reappears).
 */
export function useReturnToStuck(
  jobId: string,
): ReturnType<
  typeof useApiMutation<{ reason: string }, { ok: boolean; jobId: string; status: string }>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, { ok: boolean; jobId: string; status: string }>(
    apiOpts,
    `/v1/inreview/${jobId}/return-to-stuck`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['inreview'] });
        void qc.invalidateQueries({ queryKey: ['jobs'] });
      },
    },
  );
}

/** Escalate a ticket to TENANT_ADMIN (Task 11.5). Stays in queue; refreshes detail + queue. */
export function useEscalateTicket(
  jobId: string,
): ReturnType<typeof useApiMutation<{ reason: string }, { ok: boolean; escalated: boolean }>> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ reason: string }, { ok: boolean; escalated: boolean }>(
    apiOpts,
    `/v1/inreview/${jobId}/escalate`,
    'PATCH',
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ['inreview'] }) },
  );
}

export interface AcknowledgeFlagResponse {
  readonly ok: boolean;
  readonly alreadyAcknowledged: boolean;
  readonly acknowledgment: {
    readonly flagCode: string;
    readonly acknowledgedByName: string | null;
    readonly acknowledgedAt: string | null;
    readonly notes: string | null;
  };
}

/**
 * Acknowledge a specific flag on a ticket (Task 11.3). One hook instance per
 * flag card (the flagCode is fixed in the path). Refreshes the ticket detail on
 * success so the flag flips to acknowledged and the Approve gate re-evaluates.
 */
export function useAcknowledgeFlag(
  jobId: string,
  flagCode: string,
): ReturnType<typeof useApiMutation<{ notes?: string }, AcknowledgeFlagResponse>> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<{ notes?: string }, AcknowledgeFlagResponse>(
    apiOpts,
    `/v1/inreview/${jobId}/acknowledge/${flagCode}`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['inreview', 'detail', jobId] });
      },
    },
  );
}

export interface InReviewQueueParams {
  readonly flagType: readonly string[];
  readonly operatorId: string;
  readonly driverId: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly tz: string;
  readonly staleOnly: boolean;
  readonly sort: InReviewSortKey;
}

export interface InReviewQueueResponse {
  readonly tickets: readonly InReviewQueueRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export function inreviewQueuePath(p: InReviewQueueParams): string {
  const sp = new URLSearchParams();
  if (p.flagType.length > 0) sp.set('flagType', p.flagType.join(','));
  if (p.operatorId !== '') sp.set('operatorId', p.operatorId);
  if (p.driverId !== '') sp.set('driverId', p.driverId);
  if (p.dateFrom !== '') sp.set('dateFrom', `${p.dateFrom}T00:00:00`);
  if (p.dateTo !== '') sp.set('dateTo', `${p.dateTo}T23:59:59`);
  if (p.tz !== '') sp.set('tz', p.tz);
  if (p.staleOnly) sp.set('staleOnly', 'true');
  sp.set('sort', p.sort);
  const qs = sp.toString();
  return qs === '' ? '/v1/inreview' : `/v1/inreview?${qs}`;
}

export function useInReviewQueue(
  apiOpts: ReturnType<typeof useApiOpts>,
  params: InReviewQueueParams,
): ReturnType<typeof useApiQuery<InReviewQueueResponse>> {
  return useApiQuery<InReviewQueueResponse>(apiOpts, inreviewQueuePath(params), {
    queryKey: ['inreview', 'queue', params],
    staleTime: 10_000,
  });
}

export interface InReviewCountResponse {
  readonly count: number;
}

/** InReview count for the tab/nav badge. Poll gently so the badge stays fresh. */
export function useInReviewCount(): ReturnType<typeof useApiQuery<InReviewCountResponse>> {
  const apiOpts = useApiOpts();
  return useApiQuery<InReviewCountResponse>(apiOpts, '/v1/inreview/count', {
    queryKey: ['inreview', 'count'],
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// One definition, shared with the header bell (web-shared/notifications).
export type { InAppNotification };

/** Path for the escalated-ticket count: the queue's own filter, one row, read `total`. */
export const ESCALATED_COUNT_PATH = '/v1/inreview?flagType=ESCALATED&limit=1';

/**
 * Tickets in review RIGHT NOW that carry the ESCALATED flag — the TENANT_ADMIN
 * escalation banner's count (Task 11.6, OPEN-152). It is the same filter the
 * "View escalated tickets" button applies, so the banner can never promise
 * tickets the list then fails to show. It used to count the role's unread
 * auto-escalation NOTIFICATIONS, which outlive the flag: approving or sending
 * back a ticket clears it from review, but its notice stays unread, so the
 * banner said "4 tickets" over an empty list.
 *
 * Keyed under ['inreview'] so approve / send-back / escalate refresh it. Pass
 * `enabled=false` for non-admins so the query never fires. Polls gently.
 */
export function useEscalatedInReviewCount(
  enabled: boolean,
): ReturnType<typeof useApiQuery<InReviewQueueResponse>> {
  const apiOpts = useApiOpts();
  return useApiQuery<InReviewQueueResponse>(apiOpts, ESCALATED_COUNT_PATH, {
    queryKey: ['inreview', 'escalated-count'],
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

interface InReviewSettingsResponse {
  readonly settings: InReviewSettings;
  /**
   * OPEN-169 addendum — whether Send Back can be turned on yet (the phone's correction flow
   * shipped). While false the switch is disabled and the API refuses turning it on.
   */
  readonly sendBackAvailable?: boolean;
}

/** Fetch InReview settings. Pass `enabled=false` to skip (non-admin viewers). */
export function useInReviewSettings(
  enabled: boolean,
): ReturnType<typeof useApiQuery<InReviewSettingsResponse>> {
  const apiOpts = useApiOpts();
  return useApiQuery<InReviewSettingsResponse>(apiOpts, '/v1/tenants/inreview-settings', {
    queryKey: ['inreview', 'settings'],
    enabled,
    staleTime: 30_000,
  });
}

/** PATCH InReview settings; invalidates the settings cache on success. */
export function useUpdateInReviewSettings(): ReturnType<
  typeof useApiMutation<Partial<InReviewSettings>, InReviewSettingsResponse>
> {
  const apiOpts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Partial<InReviewSettings>, InReviewSettingsResponse>(
    apiOpts,
    '/v1/tenants/inreview-settings',
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['inreview', 'settings'] });
      },
    },
  );
}
